import CloseOutlined from '@material-ui/icons/CloseOutlined'
import LockOutlined from '@material-ui/icons/LockOutlined'
import OpenInNewOutlined from '@material-ui/icons/OpenInNewOutlined'
import { closeCreditDecisionDialog, DecisionDisplayPropsType } from 'ducks/myEnergy'
import {
  Box,
  Link,
  ArrowLeftSmIcon,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LoadingDots,
  styled,
  Theme,
  Typography,
  useMediaQuery,
} from 'opensolar-ui'
import { CheckoutFinanceAction, LenderType } from 'opensolar-ui/src/checkout/checkoutFinancing/types'
import { OPENSOLAR_LOGO_URL_HALF } from 'pages/auth/components/logos/Configs'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ExpectedMilestonePaymentType } from 'types/paymentOptions'
import MarketplaceDecisionExplanation from '../loanApplicationButton/sungage/MarketplaceDecisionExplanation'
import FinanceMarketplaceDialogContent, { ExitSurveyConfig } from './financeMarketplace/FinanceMarketplaceDialogContent'
import LenderReferralDialogContent from './lenderReferrals/LenderReferralDialogContent'
import {
  CheckoutFinancingFilterParameters,
  CheckoutFinancingPricingOption,
  CheckoutFinancingPricingParams,
} from './types'

const StyledDialog = styled(Dialog)<{ isMobile: boolean; hide: boolean }>(({ isMobile, hide }) => ({
  display: hide ? 'none' : 'block',
  '&.MuiDialog-paper': {
    width: isMobile ? '100%' : 'auto',
    margin: isMobile ? '10px' : 'auto',
  },
  '&.MuiPaper-root': {
    width: isMobile ? '100%' : 'auto',
    margin: isMobile ? '10px' : 'auto',
  },
  zIndex: 2000,
}))
const StyledDialogTitle = styled(DialogTitle)(() => ({
  borderBottom: '1px solid #B4B4B4',
  paddingBottom: '16px',
  marginLeft: '16px',
  marginRight: '16px',
  marginBottom: '16px',
}))
const DialogTitleContentContainer = styled('div')(() => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottomWidth: '1px',
}))
const DialogTitleText = styled('div')<{ isMobile: boolean }>(({ isMobile }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: isMobile ? '17px' : '18px',
  fontWeight: isMobile ? 400 : 500,
}))
const StyledCloseButton = styled(CloseOutlined)(() => ({
  color: '#757575',
}))
const LoadingDotsWrapper = styled('div')(() => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  opacity: 0.5,
  backgroundColor: '#F6F6F6',
  zIndex: 10,
}))
const StyledDialogContent = styled(DialogContent)(() => ({
  padding: '8px 24px',
}))

const StandaloneContainer = styled(Box)(() => ({
  maxWidth: 900,
  margin: '0 auto',
  padding: 24,
}))

const DialogTitleDivider = styled('span')(({ theme }: { theme: Theme }) => ({
  display: 'inline-block',
  width: '1px',
  height: '24px',
  backgroundColor: theme.palette.grey[300],
  margin: '0 12px',
}))

const DialogTitleTextTypography = styled(Typography)({
  fontWeight: 600,
})

const DialogSubtitle = styled(Typography)({
  marginBottom: '16px',
})

const HeaderRightGroup = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
})

const SecureBadge = styled('div')(({ theme }: { theme: Theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  color: theme.palette.grey[700],
  fontSize: '13px',
  '& svg': {
    fontSize: '16px',
  },
}))

const StyledViewProposalLink = styled(Link)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
})

// Wraps the header OpenSolar logo so it acts as a link to the OpenSolar marketing site on the
// standalone / shared marketplace page. `display: inline-flex` keeps the image vertically
// centred next to the "Finance Marketplace" title, and `textDecoration: none` prevents the
// underline the shared `Link` component would otherwise inherit.
const OpenSolarHomeLink = styled(Link)({
  display: 'inline-flex',
  alignItems: 'center',
  textDecoration: 'none',
  '&:hover, &:focus': {
    textDecoration: 'none',
  },
})

// Target for the logo link in the standalone marketplace header. Customers reaching the
// standalone page via a shared link are not yet inside the OpenSolar app, so the most useful
// destination is the public marketing site rather than app.opensolar.com.
const OPENSOLAR_HOME_URL = 'https://www.opensolar.com'

const CheckoutFinancingDialog = (props: {
  open: boolean
  onClose: () => void
  onApply: (lender: LenderType) => Promise<void>
  onEditLoanAmount: (field) => void
  actionData: CheckoutFinanceAction
  currencySymbol: string
  expectedMilestonePayments?: ExpectedMilestonePaymentType[] | undefined
  isLoading: boolean
  lenders: LenderType[]
  marketPlaceLenders: LenderType[]
  referralLenders: LenderType[]
  translate: (text: string) => string
  pricingOptions: CheckoutFinancingPricingOption[]
  pricingOptionsLoading: boolean
  updatePricingParams: (newParams: CheckoutFinancingPricingParams) => void
  pricingParams: CheckoutFinancingPricingParams
  filterParameters: CheckoutFinancingFilterParameters
  totalPricingOptions: number
  standalone?: boolean
  proposalUrl?: string
  fromOptimizedCheckout?: boolean
  onExitSurveyConfigLoaded?: (config: ExitSurveyConfig) => void
  onApplicationStarted?: () => void
}) => {
  const {
    open,
    onClose,
    onApply,
    onEditLoanAmount,
    actionData,
    expectedMilestonePayments,
    currencySymbol,
    isLoading,
    lenders,
    marketPlaceLenders,
    referralLenders,
    translate,
    updatePricingParams,
    pricingParams,
    filterParameters,
    totalPricingOptions,
    standalone = false,
    proposalUrl,
    fromOptimizedCheckout = false,
    onExitSurveyConfigLoaded
  } = props

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isPaymentsPage, setIsPaymentsPage] = useState(window.location.pathname.includes('/payment/'))
  const [hideDialog, setHideDialog] = useState(false)
  const [showStandaloneDecision, setShowStandaloneDecision] = useState(false)

  const displayProps: DecisionDisplayPropsType = useSelector((state: any) => state.myEnergy.decisionDisplayProps)
  const dispatch = useDispatch()

  useEffect(() => {
    if (standalone && displayProps?.decision) {
      setShowStandaloneDecision(true)
    }
  }, [standalone, displayProps?.decision])

  const closeStandaloneDecision = () => {
    setShowStandaloneDecision(false)
    dispatch(closeCreditDecisionDialog())
  }

  const handleApplyNowButton = async (lender) => {
    setIsSubmitting(true)
    await onApply(lender)
    setIsSubmitting(false)
  }

  const onCloseIfNotStandalone = () => {
    if (!standalone) {
      onClose()
    }
  }

  const handleOnClose = () => {
    if (!isSubmitting) {
      onCloseIfNotStandalone()
    }
  }

  const dialogTitleTextContent = useMemo(() => {
    return 'Finance Marketplace'
  }, [props.marketPlaceLenders])

  const dialogSubttitleTextContent = useMemo(() => {
    if (props.marketPlaceLenders?.length) {
      return 'Illustrative financial options'
    }
    return null
  }, [props.marketPlaceLenders])

  const titleBlock = standalone ? (
    <Box sx={{ borderBottom: '1px solid #B4B4B4', paddingBottom: '16px', marginBottom: '16px' }}>
      <DialogTitleContentContainer>
        <DialogTitleText isMobile={isMobile}>
          <OpenSolarHomeLink
            href={OPENSOLAR_HOME_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={translate('Visit OpenSolar')}
          >
            <img src={OPENSOLAR_LOGO_URL_HALF} alt="OpenSolar Logo" width={145} />
          </OpenSolarHomeLink>
          <DialogTitleDivider aria-hidden="true" />
          <DialogTitleTextTypography textVariant="h5">{dialogTitleTextContent}</DialogTitleTextTypography>
        </DialogTitleText>
        <HeaderRightGroup>
          {proposalUrl && (
            <StyledViewProposalLink href={proposalUrl} target="_blank">
              <OpenInNewOutlined />
              <Typography textVariant="caption1">{translate('View proposal')}</Typography>
            </StyledViewProposalLink>
          )}
          <SecureBadge aria-label={translate('Secure connection')}>
            <LockOutlined />
            <Typography textVariant="caption1">{translate('Secure')}</Typography>
          </SecureBadge>
        </HeaderRightGroup>
      </DialogTitleContentContainer>
    </Box>
  ) : (
    <StyledDialogTitle>
      <DialogTitleContentContainer>
        <DialogTitleText isMobile={isMobile}>
          <img src={OPENSOLAR_LOGO_URL_HALF} alt="OpenSolar Logo" width={145} />
          <DialogTitleTextTypography textVariant="h5">{dialogTitleTextContent}</DialogTitleTextTypography>
        </DialogTitleText>
        <div>
          <IconButton onClick={handleOnClose}>
            <StyledCloseButton />
          </IconButton>
        </div>
      </DialogTitleContentContainer>
    </StyledDialogTitle>
  )

  const contentInner = (
    <>
      {dialogSubttitleTextContent && (
        <DialogSubtitle textVariant="caption1Bold">{dialogSubttitleTextContent}</DialogSubtitle>
      )}
      {!!referralLenders?.length && (
        <LenderReferralDialogContent
          lenders={referralLenders}
          translate={translate}
          onApply={handleApplyNowButton}
          onEditLoanAmount={onEditLoanAmount}
          actionData={actionData}
          expectedMilestonePayments={expectedMilestonePayments}
          currencySymbol={currencySymbol}
          setIsSubmitting={setIsSubmitting}
        />
      )}

      {(isLoading || isSubmitting) && (
        <LoadingDotsWrapper>
          <LoadingDots />
        </LoadingDotsWrapper>
      )}

      {!!marketPlaceLenders?.length && (
        <FinanceMarketplaceDialogContent
          pricingOptions={props.pricingOptions}
          lenders={marketPlaceLenders}
          isLoading={props.pricingOptionsLoading}
          translate={translate}
          updatePricingParams={updatePricingParams}
          pricingParams={pricingParams}
          filterParameters={filterParameters}
          totalPricingOptions={totalPricingOptions}
          setHideDialog={setHideDialog}
          closeRootDialog={handleOnClose}
          onExitSurveyConfigLoaded={onExitSurveyConfigLoaded}
          fromOptimizedCheckout={fromOptimizedCheckout}
        />
      )}

      {!lenders.length && !isLoading && (
        <Typography>{translate('There are no available lenders for your region at the moment.')}</Typography>
      )}
    </>
  )

  const contentBlock = standalone ? (
    <Box sx={{ padding: '8px 0' }}>{contentInner}</Box>
  ) : (
    <StyledDialogContent>{contentInner}</StyledDialogContent>
  )

  const actionsBlock = (
    <DialogActions sx={standalone ? { paddingLeft: 0, paddingRight: 0 } : undefined}>
      <Button variant="contained" onClick={onCloseIfNotStandalone}>
        <ArrowLeftSmIcon /> {isPaymentsPage ? translate('Back to Payment page') : translate('Back to proposal')}
      </Button>
    </DialogActions>
  )

  if (standalone) {
    return (
      <StandaloneContainer>
        {titleBlock}
        {contentBlock}
        {showStandaloneDecision && displayProps?.marketplaceProductDetails && displayProps?.marketplaceProductDetails && (
          <Dialog open={true} onClose={closeStandaloneDecision}>
            <MarketplaceDecisionExplanation
              // @ts-ignore
              decision={displayProps?.decision}
              onClose={closeStandaloneDecision}
              stips={displayProps?.stips}
              // @ts-ignore
              submittedDate={displayProps?.submittedDate}
              marketplaceProductDetails={displayProps?.marketplaceProductDetails}
            />
          </Dialog>
        )}
      </StandaloneContainer>
    )
  }

  return (
    <StyledDialog
      open={open}
      onClose={handleOnClose}
      isMobile={isMobile}
      maxWidth="lg"
      fullScreen={isMobile}
      // when child dialogs are shown hide this one, we don't want to completely dismount it
      // because we want the user to be able to go back to their previous view
      hide={hideDialog}
    >
      {titleBlock}
      {contentBlock}
      {actionsBlock}
    </StyledDialog>
  )
}

export default CheckoutFinancingDialog
